/**
 * Lesson / exam visit tracking (spec docs/compound-v/specs/2026-10-26-course-student-analytics.md §Мои решения).
 *
 * Once per mount, when `enabled` (the lesson is on screen): a new visit id and one `open` event; for a lesson also
 * one `scroll` event per milestone (10, 20, … 100 % of the lesson body) the first time it is reached in this visit.
 * Server: POST /api/v1/course/lessons/:code/events and /course/exams/:code/events (204).
 *
 * Fire and forget: events go out one after another, a failure is swallowed (no retry, no error UI, no console
 * noise) and nothing here ever blocks rendering. A lost milestone is not sent again.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { apiFetch } from '../lib/api-client';
import { newMarks } from '../lib/scroll-marks';
import { useAuth } from '../store/auth';
import type { TrackingEvent } from '../types/course';

export interface LessonTrackingOptions {
  kind: 'lesson' | 'exam';
  code: string;
  /** The version on screen. Not sent: the server stamps events with the current version. */
  versionNumber: number | undefined;
  /** The lesson body whose height the milestones are counted against (lessons only). */
  contentRef: RefObject<HTMLElement | null>;
  /** True once the lesson / exam data is loaded. */
  enabled: boolean;
}

function newVisitId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* insecure context */ }
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

interface Visit {
  id: string;
  /** The item this visit belongs to: another code (a page reused for the next exam) is a new visit. */
  key: string;
  sent: Set<number>;
  /** Tail of the send queue: events leave in order, one request at a time. */
  queue: Promise<void>;
}

export function useLessonTracking({ kind, code, contentRef, enabled }: LessonTrackingOptions): void {
  const jwt = useAuth((s) => s.jwt);
  const visit = useRef<Visit | null>(null);

  useEffect(() => {
    if (!enabled || !jwt || !code) return;

    const path = `/api/v1/course/${kind === 'exam' ? 'exams' : 'lessons'}/${encodeURIComponent(code)}/events`;
    const send = (v: Visit, ev: TrackingEvent): void => {
      v.queue = v.queue.then(async () => {
        try {
          await apiFetch(path, jwt, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(ev),
          });
        } catch { /* tracking never disturbs the lesson */ }
      });
    };

    let v = visit.current;
    if (!v || v.key !== `${kind}:${code}`) {
      v = { id: newVisitId(), key: `${kind}:${code}`, sent: new Set(), queue: Promise.resolve() };
      visit.current = v;
      send(v, { visitId: v.id, kind: 'open' });
    }
    if (kind !== 'lesson') return;

    const current = v;
    let frame = 0;
    const check = (): void => {
      frame = 0;
      try {
        const el = contentRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const marks = newMarks(
          {
            scrollTop: window.scrollY,
            viewportHeight: window.innerHeight,
            contentTop: rect.top + window.scrollY,
            contentHeight: rect.height,
          },
          current.sent,
        );
        for (const depth of marks) {
          current.sent.add(depth);
          send(current, { visitId: current.id, kind: 'scroll', depth });
        }
      } catch { /* a broken measurement skips this check */ }
    };
    const schedule = (): void => {
      if (frame) return;
      frame = window.requestAnimationFrame(check);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    schedule(); // a lesson that fits on screen sends up to 100 at once
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled, jwt, kind, code, contentRef]);
}
